import { HubConnection, HubConnectionBuilder, HubConnectionState } from "@aspnet/signalr";
import { curryRight, __ } from "lodash/fp";

export class SignalR {

	public static register = async (uri: string) => {

		// Check If Connection Is Already Registered
		if (!SignalR.isRegistered(uri)) {

			// Attempt To Register To SignalR
			try {

				// Register Connection
				const connection = new HubConnectionBuilder().withUrl(uri).build();

				// Store Connection
				SignalR.connections.set(uri, connection);
			}
			catch (e) {

				// Log Error
				console.error("SignalR Error", e);
			}
		}
	}

	public static isRegistered = (uri: string): boolean => {

		// Get Previous Connection
		const existingConnection = SignalR.connections.get(uri);

		// Return Whether Registered
		return existingConnection !== undefined;
	}

	public static isStarted = (uri: string): boolean => {

		// Get Previous Connection
		const existingConnection = SignalR.connections.get(uri);

		// Return Whether Registered
		return existingConnection !== undefined && existingConnection.state === HubConnectionState.Connected;
	}

	public static listen = async (uri: string, method: string, onMethodTrigger: (...parameters: any[]) => void) => {

		// Get Previous Connection
		const existingConnection = SignalR.connections.get(uri);

		// Check Existing Connection
		if (existingConnection !== undefined) {

			// Attempt To Register Listener
			try {

				// Unregister Previous Listener
				existingConnection.off(method, onMethodTrigger);

				// Register Listener
				existingConnection.on(method, onMethodTrigger);
			}
			catch (e) {

				// Log Error
				console.error("SignalR Error", e);
			}
		}
	}

	public static start = async (uri: string) => {

		// Check If Connection Is Already Started
		if (!SignalR.isStarted(uri)) {

			// Attempt To Start Connection
			try {

				// Get Previous Connection
				const existingConnection = SignalR.connections.get(uri);

				// Check Existing Connection
				if (existingConnection !== undefined) {

					// Register On Close Handler
					existingConnection.onclose(curryRight(SignalR.onStopOccurred)(uri, __));

					// Start Connection
					await existingConnection.start();
				}
			}
			catch (e) {

				// Log Error
				console.error("SignalR Error", e);
			}
		}
	}

	public static stop = async (uri: string) => {

		// Check If Connection Is Already Started
		if (SignalR.isStarted(uri)) {

			// Attempt To Stop Connection
			try {

				// Get Previous Connection
				const existingConnection = SignalR.connections.get(uri);

				// Check Existing Connection
				if (existingConnection !== undefined) {

					// Register On Close Requested Handler
					existingConnection.onclose(curryRight(SignalR.onStopRequested)(uri, __));

					// Stop Connection
					await existingConnection.stop();
				}
			}
			catch (e) {

				// Manually Trigger Stop Handler
				SignalR.onStopRequested(uri, e);
			}
		}
	}

	public static reset = () => {

		// Loop Through Connections
		SignalR.connections.forEach((_connection, uri) => {

			// Attempt To Stop Connection
			SignalR.stop(uri);
		});

		// Clear All Connections
		SignalR.connections.clear();
	}

	private static connections: Map<string, HubConnection> = new Map<string, HubConnection>();

	private static onStopOccurred = (uri: string, error?: Error) => {

		// Check For Error
		if (error !== undefined) {

			// Log Error
			console.error("SignalR Error", error);
		}

		// Wait & Attempt To Restart Connection
		setTimeout(() => SignalR.start(uri), 10000);
	}

	private static onStopRequested = async (uri: string, error?: Error) => {

		// Check For Error
		if (error !== undefined) {

			// Log Error
			console.error("SignalR Error", error);
		}

		// Clear Connection
		SignalR.connections.delete(uri);
	}
}

// Reset Connections
SignalR.reset();
